import {
  Material,
  MaterialAPI,
  PPEEquipment,
  PPEEquipmentAPI,
  Reading,
  ReadingAPI,
  SafetyPackage,
  SafetyPackageAPI,
  SafetyProtocol,
  SafetyProtocolAPI,
  Task,
  TaskAPI,
} from "./types";

export const transformReading = (
  apiReading: ReadingAPI,
  index: number
): Reading => {
  // Create a unique ID by combining code, key, and operation_type to handle duplicates

  return {
    id: apiReading.code,
    code: apiReading.code,
    name: apiReading.name,
    workOrderType: apiReading.operationTypeDisplay,
    unit: apiReading.unit,
    minRange: apiReading.minimum,
    maxRange: apiReading.maximum,
    default: apiReading.default,
    photoRequired: apiReading.isPhotoReq === "Yes",
    createdBy: apiReading.createdBy,
    createdDate: apiReading.createdDate,
    isActive: apiReading.isActive,
    operationType: apiReading.operationType,
  };
};
export const transformMaterial = (apiMaterial: MaterialAPI): Material => {
  const createdDate = new Date(apiMaterial.createdDate);
  const isValidDate = !isNaN(createdDate.getTime());

  return {
    id: apiMaterial.code,
    name: apiMaterial.name,
    workOrderType: apiMaterial.operationTypeDisplay,
    size: apiMaterial.size,
    quantity: apiMaterial.quantity,
    costPerUnit: apiMaterial.costPerUnit,
    createdBy: apiMaterial.createdBy || "NA",
    createdDate: apiMaterial.createdDate,
    isActive: apiMaterial.isActive, // Default to true since API doesn't have this field
    operationType: parseInt(apiMaterial.operationType),
  };
};
export const transformTask = (apiTask: TaskAPI): Task => {
  const createdDate = new Date(apiTask.createdDate || new Date());
  const isValidDate = !isNaN(createdDate.getTime());

  return {
    id: apiTask.code,
    name: apiTask.name,
    workOrderType: apiTask.operationTypeDisplay,
    turnaroundTime: parseInt(apiTask.time) || 0,
    downtime: parseInt(apiTask.downtime) || 0,
    skillIds: apiTask.skillId,
    photoRequired: apiTask.isPhotoReq === "Yes",
    createdBy: apiTask.createdBy || "Unknown",
    createdDate: apiTask.createdDate,
    isActive: apiTask.isActive,
    operationType: apiTask.operationType,
  };
};
export const transformPPEEquipment = (
  apiEquipment: PPEEquipmentAPI
): PPEEquipment => {
  const createdDate = new Date(apiEquipment.createdDate);
  const isValidDate = !isNaN(createdDate.getTime());

  return {
    id: apiEquipment.id,
    name: apiEquipment.name,
    description: apiEquipment.description,
    createdBy: apiEquipment.createdUserRemoteName || "NA",
    createdDate: apiEquipment.createdDate || "NA",
    isActive: apiEquipment.isActive,
    type: "",
  };
};
export const transformSafetyProtocol = (
  apiProtocol: SafetyProtocolAPI
): SafetyProtocol => {
  const createdDate = new Date(apiProtocol.createdDate);
  const isValidDate = !isNaN(createdDate.getTime());

  return {
    id: apiProtocol.id,
    name: apiProtocol.name,
    description: apiProtocol.description,
    createdBy: apiProtocol.createdUserRemoteName,
    createdDate: apiProtocol.createdDate,
    isActive: apiProtocol.isActive,
  };
};

// Transform API data to component format
export const transformSafetyPackage = (
  apiPackage: SafetyPackageAPI
): SafetyPackage => {
  return {
    id: apiPackage.id,
    name: apiPackage.name,
    description: apiPackage.description,
    protocols: apiPackage.safetyProtocol.map((id) => `Protocol ${id}`),
    equipment: apiPackage.safetyEquipment.map((id) => `Equipment ${id}`),
    createdUserRemoteName: apiPackage.createdUserRemoteName,
    createdDate: apiPackage.createdDate, // API doesn't provide creation date
    isActive: apiPackage.isActive,
  };
};
export const transformSafetyPackageWithLookup = (
  apiPackage: SafetyPackageAPI,
  safetyProtocols: SafetyProtocol[] = [],
  ppeEquipment: PPEEquipment[] = []
): SafetyPackage => {
  // Create lookup maps for faster searching
  const protocolLookup = new Map(safetyProtocols.map((p) => [p.id, p.name]));
  const equipmentLookup = new Map(ppeEquipment.map((e) => [e.id, e.name]));

  return {
    id: apiPackage.id,
    name: apiPackage.name,
    description: apiPackage.description,
    // Map IDs to actual names, fallback to generic if not found
    protocols: apiPackage.safetyProtocol.map(
      (id) => protocolLookup.get(id) || `Protocol ${id}`
    ),
    equipment: apiPackage.safetyEquipment.map(
      (id) => equipmentLookup.get(id) || `Equipment ${id}`
    ),
    createdUserRemoteName: apiPackage.createdUserRemoteName,
    createdDate: apiPackage.createdDate || "NA",
    isActive: apiPackage.isActive,
  };
};
